import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Play, Sparkles, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { ImageForm } from "@/components/create/ImageForm";
import { QaNotice } from "@/components/create/StudioNotices";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { api } from "@/lib/api";
import { presetOpen, useMediaConfig, usePresets } from "@/lib/studio";

const STATUS_NOTE = {
  pending: "Only you can see this preset while it's being reviewed.",
  rejected: "This preset wasn't approved, so only you can see it.",
};

function Cover({ preset }) {
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden bg-black md:aspect-auto md:h-full md:min-h-[460px]">
      <img src={preset.cover_url} alt={preset.title} className="absolute inset-0 h-full w-full object-cover animate-ken-burns" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e12] via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-[#0e0e12]/40" />
      {preset.kind === "video" && <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur"><Play className="h-3 w-3 fill-current" /> Still preview</span>}
      {preset.example_url && (
        <div className="absolute bottom-4 left-4 w-24 overflow-hidden rounded-xl border border-white/30 shadow-xl sm:w-28" data-testid="preset-sheet-before">
          <img src={preset.example_url} alt="Before" className="aspect-square w-full object-cover" />
          <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[9px] font-bold uppercase tracking-wider text-white">Before</span>
        </div>
      )}
    </div>
  );
}

function Locked({ preset }) {
  const video = preset.kind === "video";
  return (
    <div className="space-y-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4" data-testid="preset-coming-soon">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-300"><Sparkles className="h-3.5 w-3.5" /> Coming soon</p>
      <p className="text-sm text-zinc-300">{video ? "Video creation opens soon. You'll be able to launch this preset as a short clip." : "Image creation and editing open soon. You'll be able to launch this preset in one tap."}</p>
      <div className="rounded-xl bg-ink-900 p-3">
        <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-500">The prompt</p>
        <p className="text-sm leading-relaxed text-zinc-300" data-testid="preset-locked-prompt">{preset.prompt}</p>
      </div>
    </div>
  );
}

function RemoveMine({ preset, onDone }) {
  const qc = useQueryClient();
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  const remove = useMutation({
    mutationFn: () => api.del(`/studio/presets/${preset.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["studio-presets"] }); toast.success("Preset removed"); onDone(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <button type="button" onClick={() => (armed ? remove.mutate() : setArmed(true))} disabled={remove.isPending} data-testid="preset-remove-button"
      className={`inline-flex items-center gap-1.5 text-xs transition-colors ${armed ? "text-red-300" : "text-zinc-500 hover:text-red-300"}`}>
      {remove.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} {armed ? "Tap again to remove it for everyone" : "Remove my preset"}
    </button>
  );
}

function PresetDetail({ preset, config, onClose }) {
  const navigate = useNavigate();
  const open = presetOpen(preset, config);
  const edit = preset.operation === "edit";
  const launched = () => { onClose(); navigate(`/create/image${edit ? "?mode=edit" : ""}`); };
  return (
    <div className="max-h-[88dvh] overflow-y-auto md:grid md:h-[min(86vh,720px)] md:max-h-none md:grid-cols-[1.05fr_1fr] md:grid-rows-[minmax(0,1fr)] md:overflow-hidden" data-testid="preset-sheet">
      <Cover preset={preset} />
      <div className="space-y-4 p-5 sm:p-6 md:h-full md:overflow-y-auto">
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
            {preset.source === "community" ? <><Users className="h-3.5 w-3.5" /> Community</> : "ChatSleek preset"} · {preset.category}
          </p>
          <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl" data-testid="preset-sheet-title">{preset.title}</h2>
          {preset.description && <p className="text-sm text-zinc-400">{preset.description}</p>}
          {STATUS_NOTE[preset.status] && <p className="text-xs text-amber-200/80" data-testid="preset-status-note">{STATUS_NOTE[preset.status]}</p>}
        </div>
        {!open && <Locked preset={preset} />}
        {open && config.image.access === "qa" && <QaNotice budget={config.image.qa_budget} />}
        {open && (
          <ImageForm mode={preset.operation} offer={config.image} compact onCreated={launched}
            initial={{ prompt: preset.prompt, quality: preset.quality || "budget", aspect: edit ? "auto" : preset.aspect_ratio }} />
        )}
        {preset.mine && <RemoveMine preset={preset} onDone={onClose} />}
      </div>
    </div>
  );
}

export function PresetSheet() {
  const [params, setParams] = useSearchParams();
  const id = params.get("preset");
  const presets = usePresets();
  const config = useMediaConfig();
  const desktop = useMediaQuery("(min-width: 768px)");
  if (!id) return null;
  const preset = (presets.data?.items || []).find((p) => p.id === id || p.slug === id);
  const close = () => setParams((prev) => { prev.delete("preset"); return prev; }, { replace: true });
  const ready = preset && config.data;
  const body = ready ? <PresetDetail preset={preset} config={config.data} onClose={close} /> : (
    <div className="flex h-60 items-center justify-center text-sm text-zinc-500" data-testid="preset-sheet-loading">
      {presets.isLoading || config.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "This preset isn't available any more."}
    </div>
  );
  const title = <span className="sr-only">{preset?.title || "Preset"}</span>;
  if (desktop) {
    return (
      <Dialog open onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-4xl gap-0 overflow-hidden rounded-3xl border-white/15 bg-[#0e0e12] p-0 shadow-[0_30px_80px_rgba(0,0,0,0.85)]">
          <DialogTitle asChild>{title}</DialogTitle>
          {body}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open onOpenChange={(o) => !o && close()} shouldScaleBackground={false}>
      <DrawerContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[94dvh] rounded-t-3xl border-white/15 bg-[#0e0e12] [&>div:first-child]:bg-white/20">
        <DrawerTitle asChild>{title}</DrawerTitle>
        {body}
      </DrawerContent>
    </Drawer>
  );
}
